
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Category } from '@/types/category';
import AddCategoryDialog from '@/components/admin/categories/AddCategoryDialog';
import EditCategoryDialog from '@/components/admin/categories/EditCategoryDialog';
import CategoryCard from '@/components/admin/categories/CategoryCard';

const AdminCategories = () => {
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const queryClient = useQueryClient();

  // Fetch categories with better error handling
  const { data: categories = [], isLoading, error, refetch } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('order_index');

      if (error) {
        console.error('Error fetching categories:', error);
        throw error;
      }
      return data || [];
    },
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30 * 1000, // 30 seconds
    refetchOnMount: true,
  });

  // Delete category mutation
  const deleteCategoryMutation = useMutation({
    mutationFn: async (categoryId: string) => {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', categoryId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast.success('Category deleted successfully');
    },
    onError: (error: any) => {
      console.error('Delete category error:', error);
      toast.error(`Failed to delete category: ${error.message || 'Unknown error'}`);
    },
  });

  const handleDeleteCategory = (categoryId: string) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      deleteCategoryMutation.mutate(categoryId);
    }
  };

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <Card className="p-6 text-center max-w-md">
          <h3 className="text-lg font-semibold text-red-600 mb-2">Error Loading Categories</h3>
          <p className="text-gray-600 mb-4">
            {error?.message || 'Failed to load categories. This might be a connection issue.'}
          </p>
          <div className="flex gap-2 justify-center">
            <Button onClick={() => refetch()} variant="outline">
              Try Again
            </Button>
            <Button onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Categories</h1>
          <p className="text-gray-600 mt-1">Organize your games into categories</p>
        </div>
        <AddCategoryDialog categoriesCount={categories.length} />
      </div>

      {/* Edit Category Dialog */}
      <EditCategoryDialog 
        category={editingCategory}
        onClose={() => setEditingCategory(null)}
      />

      {/* Categories Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {isLoading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : categories.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            <h3 className="text-lg font-semibold mb-2">No categories found</h3>
            <p>Create your first category to get started organizing your games.</p>
          </div>
        ) : (
          categories.map((category) => (
            <CategoryCard 
              key={category.id} 
              category={category}
              onEdit={setEditingCategory}
              onDelete={handleDeleteCategory}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default AdminCategories;
